/**
 * OpenWeatherIconPresenter - Stateless weather icon component for JHP
 *
 * This is a self-contained version of OpenWeatherIcon that bundles the
 * weather icon SVGs and theme colors, allowing it to be federated to JHP
 * without dependencies on Zoomin's theme system.
 */

import * as React from 'react'

import InlineSVG from 'svg-inline-react'

import ClearNightIconSrc from '~/client/static/icons/weather-icons/clear-night.svg'
import CloudyIconSrc from '~/client/static/icons/weather-icons/cloudy.svg'
import DewPointSrc from '~/client/static/icons/weather-icons/dew-point.svg'
import FogIconSrc from '~/client/static/icons/weather-icons/fog.svg'
import HumidityIconSrc from '~/client/static/icons/weather-icons/humidity.svg'
import PartlyCloudyNightIconSrc from '~/client/static/icons/weather-icons/partly-cloudy-night.svg'
import PartlyCloudyIconSrc from '~/client/static/icons/weather-icons/partly-cloudy.svg'
import RainIconSrc from '~/client/static/icons/weather-icons/rain-heavy.svg'
import ShowerIconSrc from '~/client/static/icons/weather-icons/rain-light.svg'
import SnowIconSrc from '~/client/static/icons/weather-icons/snow-cloudy.svg'
import SunnyIconSrc from '~/client/static/icons/weather-icons/sunny.svg'
import ThunderstormIconSrc from '~/client/static/icons/weather-icons/thunderstorm.svg'
import WindDirectionIconSrc from '~/client/static/icons/weather-icons/wind-direction.svg'

// Theme colors from Zoomin's theme.module.scss (hardcoded for federation)
// These correspond to the design system colors used for weather icons
const THEME_COLORS = {
  secondary50: '#f5a623', // Orange/sun color
  neutral50: '#62626c', // Grey for clouds
  primary20: '#093f9b', // Dark blue for night
  primary60: '#00b5ec', // Light blue for rain
  primary70: '#4379ab', // Blue-grey for snow
}

interface IProps {
  openWeatherIconCode: string
  className?: string
  size?: number
  isColor?: boolean
}

type IconRenderer = (props: {
  className?: string
  color?: string
}) => JSX.Element

type IconsMapAndColors = {
  [key: string]: [IconRenderer, string]
}

const DEFAULT_SIZE = 24

// Icon component factory
const createIconRenderer =
  (src: string) =>
  ({
    className,
    color,
  }: {
    className?: string
    color?: string
  }): JSX.Element => (
    <InlineSVG
      className={className}
      src={src}
      style={{ color, width: '100%', height: '100%' }}
    />
  )

// Map of OpenWeather icon codes to icon components and their colors
const iconsMap: IconsMapAndColors = {
  '01d': [createIconRenderer(SunnyIconSrc), THEME_COLORS.secondary50],
  '02d': [createIconRenderer(PartlyCloudyIconSrc), THEME_COLORS.secondary50],
  '03d': [createIconRenderer(CloudyIconSrc), THEME_COLORS.neutral50],
  '04d': [createIconRenderer(CloudyIconSrc), THEME_COLORS.neutral50],
  '09d': [createIconRenderer(RainIconSrc), THEME_COLORS.primary60],
  '10d': [createIconRenderer(ShowerIconSrc), THEME_COLORS.primary60],
  '11d': [createIconRenderer(ThunderstormIconSrc), THEME_COLORS.primary60],
  '13d': [createIconRenderer(SnowIconSrc), THEME_COLORS.primary70],
  '50d': [createIconRenderer(FogIconSrc), THEME_COLORS.neutral50],
  '01n': [createIconRenderer(ClearNightIconSrc), THEME_COLORS.primary20],
  '02n': [createIconRenderer(PartlyCloudyNightIconSrc), THEME_COLORS.primary20],
  '03n': [createIconRenderer(CloudyIconSrc), THEME_COLORS.neutral50],
  '04n': [createIconRenderer(CloudyIconSrc), THEME_COLORS.neutral50],
  '09n': [createIconRenderer(RainIconSrc), THEME_COLORS.primary60],
  '10n': [createIconRenderer(ShowerIconSrc), THEME_COLORS.primary60],
  '11n': [createIconRenderer(ThunderstormIconSrc), THEME_COLORS.primary60],
  '13n': [createIconRenderer(SnowIconSrc), THEME_COLORS.primary70],
  '50n': [createIconRenderer(FogIconSrc), THEME_COLORS.neutral50],
}

/**
 * Renders weather icons based on OpenWeather icon codes
 */
export default function OpenWeatherIconPresenter({
  openWeatherIconCode,
  className,
  size = DEFAULT_SIZE,
  isColor = false,
}: IProps): JSX.Element | null {
  if (!openWeatherIconCode || !iconsMap[openWeatherIconCode]) {
    return null
  }

  const [iconRenderer, color] = iconsMap[openWeatherIconCode]

  if (!iconRenderer) {
    return null
  }

  return (
    <div
      className={className}
      style={{ width: size, height: size, display: 'inline-flex' }}
    >
      {iconRenderer({ color: isColor ? color : undefined })}
    </div>
  )
}

// Export utility icons (Humidity, DewPoint) for data point display
export function HumidityIcon({
  className,
  size = 12,
}: {
  className?: string
  size?: number
}): JSX.Element {
  return (
    <div
      className={className}
      style={{ width: size, height: size, display: 'inline-flex' }}
    >
      <InlineSVG
        src={HumidityIconSrc}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  )
}

export function DewPointIcon({
  className,
  size = 12,
}: {
  className?: string
  size?: number
}): JSX.Element {
  return (
    <div
      className={className}
      style={{ width: size, height: size, display: 'inline-flex' }}
    >
      <InlineSVG src={DewPointSrc} style={{ width: '100%', height: '100%' }} />
    </div>
  )
}

export function WindDirectionIcon({
  className,
  size = 12,
  degree = 0,
  style,
}: {
  className?: string
  size?: number
  degree?: number
  style?: React.CSSProperties
}): JSX.Element {
  return (
    <div
      className={className}
      style={{
        width: size,
        height: size,
        display: 'inline-flex',
        transform: `rotate(${degree}deg)`,
        ...style,
      }}
    >
      <InlineSVG
        src={WindDirectionIconSrc}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  )
}
